import type { ServicePackageCard } from "../../pages/settings/services-catalog";
import type {
  NotificationPreferenceView,
  NotificationView,
  OwnerAlertSettingsView,
} from "../notification-types";
import type {
  ConsoleDecisionResult,
  ConsoleMessagePage,
  ConsoleSendResult,
  ConsoleThreadView,
} from "../console-types";
import type {
  UpsellPromptView,
  ChecklistView,
  GalleryImportResult,
  GalleryListView,
  UiPrefsView,
} from "../onboarding-types";
import type {
  ShortLinkView,
  SocialPostView,
  BranchListView,
  BranchView,
  WidgetConfigView,
  SiteFormSubmissionView,
  SiteFormView,
  AuditLogPage,
  BusinessHours,
  DeletionStatus,
  OnboardingState,
  StarterPackInfo,
  StarterPackResult,
  TestMessageDelivery,
  Workspace,
  WorkspaceModulesView,
  WorkspaceExport,
  WorkspaceSummary,
} from "../types";
import type { SupportTicketSummary, SupportTicketThread } from "../support-types";
import type { SallaLinkResult, SallaOnboardingState, SallaPendingInstall } from "../salla-types";
import type { ApiClient } from "../api-client";
import type { HttpTransport } from "../http/transport";
import type { ChangelogEntryView, SampleDataStatus } from "../client-types";

export type WorkspaceApiMethods = Pick<
  ApiClient,
  | "listWorkspaces"
  | "createWorkspace"
  | "getWorkspace"
  | "getWorkspaceModules"
  | "updateWorkspaceModules"
  | "updateWorkspace"
  | "requestDeletion"
  | "cancelDeletion"
  | "createExport"
  | "getBusinessHours"
  | "updateBusinessHours"
  | "applyEidPreset"
  | "updateOnboarding"
  | "sendTestMessage"
  | "getTestMessageStatus"
  | "listStarterPacks"
  | "getSallaOnboarding"
  | "activateSallaStarterFlow"
  | "completeSallaOnboarding"
  | "getSallaPendingInstall"
  | "connectSallaExisting"
  | "createSallaWorkspace"
  | "applyStarterPack"
  | "uploadLogo"
  | "getWidgetConfig"
  | "updateWidgetConfig"
  | "listSocialPosts"
  | "createSocialPost"
  | "socialPostAction"
  | "scheduleSocialPost"
  | "deleteSocialPost"
  | "suggestSocialCaptions"
  | "listWidgets"
  | "createWidget"
  | "listSiteForms"
  | "createSiteForm"
  | "updateSiteForm"
  | "deleteSiteForm"
  | "listSiteFormSubmissions"
  | "listShortLinks"
  | "createShortLink"
  | "updateShortLink"
  | "deleteShortLink"
  | "bulkCreateShortLinks"
  | "shortLinkQrUrl"
  | "listBranches"
  | "createBranch"
  | "updateBranch"
  | "exportAuditLog"
  | "pushSubscribe"
  | "pushTest"
  | "pushConfig"
  | "listNotifications"
  | "getUnreadNotificationCount"
  | "getConsoleThread"
  | "listConsoleMessages"
  | "sendConsoleMessage"
  | "decideConsoleProposal"
  | "markConsoleRead"
  | "getConsoleUnreadCount"
  | "markNotificationRead"
  | "markAllNotificationsRead"
  | "getNotificationPreferences"
  | "saveNotificationPreferences"
  | "getOwnerAlertSettings"
  | "saveOwnerAlertSettings"
  | "listChangelog"
  | "listSupportTickets"
  | "getSupportTicket"
  | "createSupportTicket"
  | "sendSupportMessage"
  | "getSampleData"
  | "seedSampleData"
  | "removeSampleData"
  | "getOnboardingChecklist"
  | "dismissOnboardingChecklist"
  | "reopenOnboardingChecklist"
  | "getUpsellPrompt"
  | "dismissUpsellPrompt"
  | "listServicePackages"
  | "requestServicePackage"
  | "getUiPrefs"
  | "dismissUiLayer"
  | "listGalleryPacks"
  | "importGalleryPack"
  | "pushUnsubscribe"
  | "listAuditLog"
>;

export function createWorkspaceApi({
  request,
  requestCsv,
  ws,
  queryString,
  base,
}: HttpTransport): WorkspaceApiMethods {
  return {
    async listWorkspaces() {
      return (await request<{ workspaces: WorkspaceSummary[] }>("GET", "/v1/workspaces"))
        .workspaces;
    },
    createWorkspace: (input) => request<Workspace>("POST", "/v1/workspaces", input),
    getWorkspace: (id) => request<Workspace>("GET", ws(id)),
    getWorkspaceModules: (id) => request<WorkspaceModulesView>("GET", `${ws(id)}/modules`),
    updateWorkspaceModules: (id, modules) =>
      request<WorkspaceModulesView>("PUT", `${ws(id)}/modules`, { modules }),
    updateWorkspace: (id, input) => request<Workspace>("PATCH", ws(id), input),
    requestDeletion: (id, input) =>
      request<DeletionStatus>("POST", `${ws(id)}/deletion/request`, input),
    cancelDeletion: (id) => request<DeletionStatus>("POST", `${ws(id)}/deletion/cancel`),
    createExport: (id) => request<WorkspaceExport>("POST", `${ws(id)}/export`),
    getBusinessHours: (id) => request<BusinessHours>("GET", `${ws(id)}/business-hours`),
    updateBusinessHours: (id, input) =>
      request<BusinessHours>("PATCH", `${ws(id)}/business-hours`, input),
    applyEidPreset: (id) =>
      request<BusinessHours & { added: number }>("POST", `${ws(id)}/business-hours/eid-preset`),
    updateOnboarding: (id, input) => request<OnboardingState>("PUT", `${ws(id)}/onboarding`, input),
    sendTestMessage: (id, phone, channelId) =>
      request<{ deliveryId: string }>("POST", `${ws(id)}/onboarding/test-message`, {
        phone,
        ...(channelId ? { channelId } : {}),
      }),
    getTestMessageStatus: (id, deliveryId) =>
      request<TestMessageDelivery>(
        "GET",
        `${ws(id)}/onboarding/test-message/${encodeURIComponent(deliveryId)}`,
      ),
    listStarterPacks: (id) =>
      request<{ items: StarterPackInfo[] }>("GET", `${ws(id)}/onboarding/starter-packs`),
    getSallaOnboarding: (id) =>
      request<SallaOnboardingState>("GET", `${ws(id)}/apps/salla/onboarding`),
    activateSallaStarterFlow: (id, packKey) =>
      request<SallaOnboardingState>("POST", `${ws(id)}/apps/salla/onboarding/starter-flow`, {
        packKey,
      }),
    completeSallaOnboarding: (id) =>
      request<SallaOnboardingState>("POST", `${ws(id)}/apps/salla/onboarding/complete`),
    getSallaPendingInstall: () =>
      request<SallaPendingInstall>("GET", "/v1/apps/salla/install/pending"),
    connectSallaExisting: (id) =>
      request<SallaLinkResult>("POST", `${ws(id)}/apps/salla/install/connect-existing`),
    createSallaWorkspace: () =>
      request<SallaLinkResult>("POST", "/v1/apps/salla/install/create-new"),
    applyStarterPack: (id, packKey) =>
      request<StarterPackResult>("POST", `${ws(id)}/onboarding/starter-pack`, { packKey }),
    uploadLogo: (id, file) =>
      request<Workspace>("POST", `${ws(id)}/logo`, undefined, {
        body: file,
        contentType: file.type || "application/octet-stream",
      }),
    getWidgetConfig: (id) => request<WidgetConfigView>("GET", `${ws(id)}/growth/widget-config`),
    updateWidgetConfig: (id, input, embedKey) =>
      request<WidgetConfigView>(
        "PATCH",
        embedKey
          ? `${ws(id)}/growth/widgets/${encodeURIComponent(embedKey)}`
          : `${ws(id)}/growth/widget-config`,
        input,
      ),
    listWidgets: async (id) =>
      (await request<{ widgets: WidgetConfigView[] }>("GET", `${ws(id)}/growth/widgets`)).widgets,
    createWidget: (id, name) =>
      request<WidgetConfigView>("POST", `${ws(id)}/growth/widgets`, { name }),
    listSiteForms: async (id) =>
      (await request<{ items: SiteFormView[] }>("GET", `${ws(id)}/growth/site-forms`)).items,
    createSiteForm: (id, input) =>
      request<SiteFormView>("POST", `${ws(id)}/growth/site-forms`, input),
    updateSiteForm: (id, formId, input) =>
      request<SiteFormView>(
        "PATCH",
        `${ws(id)}/growth/site-forms/${encodeURIComponent(formId)}`,
        input,
      ),
    deleteSiteForm: async (id, formId) => {
      await request("DELETE", `${ws(id)}/growth/site-forms/${encodeURIComponent(formId)}`);
    },
    listSiteFormSubmissions: async (id, formId) =>
      (
        await request<{ items: SiteFormSubmissionView[] }>(
          "GET",
          `${ws(id)}/growth/site-forms/${encodeURIComponent(formId)}/submissions`,
        )
      ).items,
    listSocialPosts: async (id, filter) =>
      (
        await request<{ items: SocialPostView[] }>(
          "GET",
          `${ws(id)}/growth/social-posts${filter ? `?${new URLSearchParams(filter as Record<string, string>)}` : ""}`,
        )
      ).items,
    createSocialPost: async (id, input) =>
      (await request<{ post: SocialPostView }>("POST", `${ws(id)}/growth/social-posts`, input))
        .post,
    socialPostAction: async (id, postId, action) =>
      (
        await request<{ post: SocialPostView }>(
          "POST",
          `${ws(id)}/growth/social-posts/${encodeURIComponent(postId)}/${action}`,
          {},
        )
      ).post,
    scheduleSocialPost: async (id, postId, scheduledAt) =>
      (
        await request<{ post: SocialPostView }>(
          "POST",
          `${ws(id)}/growth/social-posts/${encodeURIComponent(postId)}/schedule`,
          { scheduledAt },
        )
      ).post,
    deleteSocialPost: async (id, postId) => {
      await request("DELETE", `${ws(id)}/growth/social-posts/${encodeURIComponent(postId)}`);
    },
    suggestSocialCaptions: async (id, input) =>
      (
        await request<{ captions: string[] }>(
          "POST",
          `${ws(id)}/growth/social-posts/caption-suggestions`,
          input,
        )
      ).captions,
    listShortLinks: async (id) =>
      (await request<{ links: ShortLinkView[] }>("GET", `${ws(id)}/growth/short-links`)).links,
    createShortLink: (id, input) =>
      request<ShortLinkView>("POST", `${ws(id)}/growth/short-links`, input),
    updateShortLink: (id, linkId, input) =>
      request<ShortLinkView>(
        "PATCH",
        `${ws(id)}/growth/short-links/${encodeURIComponent(linkId)}`,
        input,
      ),
    deleteShortLink: async (id, linkId) => {
      await request("DELETE", `${ws(id)}/growth/short-links/${encodeURIComponent(linkId)}`);
    },
    bulkCreateShortLinks: async (id, csv) =>
      (
        await request<{ links: ShortLinkView[] }>("POST", `${ws(id)}/growth/short-links/bulk`, {
          csv,
        })
      ).links,
    shortLinkQrUrl: (id, linkId, query) => {
      const params = new URLSearchParams({
        format: query.format,
        size: query.size,
        frame: String(query.frame),
      });
      if (query.cta) params.set("cta", query.cta);
      if (query.source) params.set("source", query.source);
      return `${base}${ws(id)}/growth/short-links/${encodeURIComponent(linkId)}/qr?${params}`;
    },
    listBranches: (id) => request<BranchListView>("GET", `${ws(id)}/branches`),
    createBranch: (id, input) => request<BranchView>("POST", `${ws(id)}/branches`, input),
    updateBranch: (id, branchId, input) =>
      request<BranchView>("PUT", `${ws(id)}/branches/${branchId}`, input),
    exportAuditLog: (id, query) =>
      requestCsv(
        "GET",
        `${ws(id)}/audit-log/export${queryString(query as Record<string, string | number | undefined> | undefined)}`,
      ),
    pushSubscribe: async (id, subscription) => {
      await request<unknown>("POST", `${ws(id)}/notifications/push-subscribe`, subscription);
    },
    pushTest: (id) =>
      request<{ delivered: number }>("POST", `${ws(id)}/notifications/push-test`, {}),
    pushConfig: () => request<{ publicKey: string | null }>("GET", "/v1/notifications/push-config"),
    listNotifications: async (id, unreadOnly, category, limit) => {
      const query = new URLSearchParams();
      if (unreadOnly) query.set("unread", "true");
      if (category) query.set("category", category);
      if (limit) query.set("limit", String(limit));
      const qs = query.toString();
      return (
        await request<{ notifications: NotificationView[] }>(
          "GET",
          `${ws(id)}/notifications${qs ? `?${qs}` : ""}`,
        )
      ).notifications;
    },
    getUnreadNotificationCount: async (id) =>
      (await request<{ count: number }>("GET", `${ws(id)}/notifications/unread-count`)).count,
    getConsoleThread: (id) => request<ConsoleThreadView>("GET", `${ws(id)}/console/thread`),
    listConsoleMessages: (id, opts) => {
      const query = new URLSearchParams();
      if (opts?.cursor) query.set("cursor", opts.cursor);
      if (opts?.limit) query.set("limit", String(opts.limit));
      const qs = query.toString();
      return request<ConsoleMessagePage>("GET", `${ws(id)}/console/messages${qs ? `?${qs}` : ""}`);
    },
    sendConsoleMessage: (id, text) =>
      request<ConsoleSendResult>("POST", `${ws(id)}/console/messages`, { text }),
    decideConsoleProposal: (id, messageId, verb) =>
      request<ConsoleDecisionResult>(
        "POST",
        `${ws(id)}/console/proposals/${encodeURIComponent(messageId)}/${verb}`,
        {},
      ),
    markConsoleRead: async (id) =>
      (await request<{ marked: number }>("POST", `${ws(id)}/console/read`, {})).marked,
    getConsoleUnreadCount: async (id) =>
      (await request<{ count: number }>("GET", `${ws(id)}/console/unread-count`)).count,
    markNotificationRead: async (id, notificationId) => {
      await request<unknown>(
        "POST",
        `${ws(id)}/notifications/${encodeURIComponent(notificationId)}/read`,
      );
    },
    markAllNotificationsRead: async (id) => {
      await request<unknown>("POST", `${ws(id)}/notifications/read-all`);
    },
    getNotificationPreferences: async (id) =>
      (
        await request<{ preferences: NotificationPreferenceView[] }>(
          "GET",
          `${ws(id)}/notifications/preferences`,
        )
      ).preferences,
    saveNotificationPreferences: async (id, items) =>
      (
        await request<{ preferences: NotificationPreferenceView[] }>(
          "PUT",
          `${ws(id)}/notifications/preferences`,
          { items },
        )
      ).preferences,
    getOwnerAlertSettings: async (id) =>
      (
        await request<{ settings: OwnerAlertSettingsView }>(
          "GET",
          `${ws(id)}/notifications/owner-alerts`,
        )
      ).settings,
    saveOwnerAlertSettings: async (id, input) =>
      (
        await request<{ settings: OwnerAlertSettingsView }>(
          "PUT",
          `${ws(id)}/notifications/owner-alerts`,
          input,
        )
      ).settings,
    listChangelog: async () =>
      (await request<{ entries: ChangelogEntryView[] }>("GET", "/v1/changelog")).entries,
    async listSupportTickets(id) {
      return (
        await request<{ tickets: SupportTicketSummary[] }>("GET", `${ws(id)}/support/tickets`)
      ).tickets;
    },
    getSupportTicket: (id, ticketId) =>
      request<SupportTicketThread>(
        "GET",
        `${ws(id)}/support/tickets/${encodeURIComponent(ticketId)}`,
      ),
    createSupportTicket: (id, input) =>
      request<SupportTicketSummary>("POST", `${ws(id)}/support/tickets`, input),
    sendSupportMessage: (id, input) => request("POST", `${ws(id)}/support/widget/messages`, input),
    getSampleData: (id) => request<SampleDataStatus>("GET", `${ws(id)}/onboarding/sample-data`),
    seedSampleData: (id) =>
      request<SampleDataStatus>("POST", `${ws(id)}/onboarding/sample-data/seed`),
    removeSampleData: (id) =>
      request<SampleDataStatus>("DELETE", `${ws(id)}/onboarding/sample-data`),
    getOnboardingChecklist: (id) => request<ChecklistView>("GET", `${ws(id)}/onboarding/checklist`),
    dismissOnboardingChecklist: (id) =>
      request<ChecklistView>("POST", `${ws(id)}/onboarding/checklist/dismiss`),
    reopenOnboardingChecklist: (id) =>
      request<ChecklistView>("POST", `${ws(id)}/onboarding/checklist/reopen`),
    getUpsellPrompt: async (id) =>
      (
        await request<{ prompt: UpsellPromptView | null }>(
          "GET",
          `${ws(id)}/onboarding/upsell-prompt`,
        )
      ).prompt,
    dismissUpsellPrompt: async (id, promptKey) => {
      await request("POST", `${ws(id)}/onboarding/upsell-prompt/dismiss`, { promptKey });
    },
    listServicePackages: async (id) =>
      (await request<{ packages: ServicePackageCard[] }>("GET", `${ws(id)}/services/packages`))
        .packages,
    requestServicePackage: async (id, input) => {
      await request("POST", `${ws(id)}/services/requests`, input);
    },
    getUiPrefs: (id) => request<UiPrefsView>("GET", `${ws(id)}/ui-prefs`),
    dismissUiLayer: (id, layer) =>
      request<UiPrefsView>("POST", `${ws(id)}/ui-prefs/dismiss/${encodeURIComponent(layer)}`),
    listGalleryPacks: (id, options) => {
      const params = new URLSearchParams();
      if (options?.industry) params.set("industry", options.industry);
      if (options?.all) params.set("all", "1");
      const qs = params.toString();
      return request<GalleryListView>("GET", `${ws(id)}/onboarding/gallery${qs ? `?${qs}` : ""}`);
    },
    importGalleryPack: (id, input) =>
      request<GalleryImportResult>("POST", `${ws(id)}/onboarding/gallery/import`, input),
    pushUnsubscribe: async (id, endpoint) => {
      await request<unknown>("DELETE", `${ws(id)}/notifications/push-subscribe`, { endpoint });
    },
    listAuditLog: (id, query) =>
      request<AuditLogPage>(
        "GET",
        `${ws(id)}/audit-log${queryString(query as Record<string, string | number | undefined> | undefined)}`,
      ),
  };
}
